/**
 * useVendorCategories — fetches Control Room's canonical vendor-category
 * list for the user's current org + selected outlet.
 *
 * Architectural rules enforced:
 *  - Categories never come from Bulk Capture local DB.
 *  - List is fetched only AFTER login context is loaded AND outlet is selected.
 *  - Cache key is (org_id, outlet_id). Switching outlets refetches.
 *  - Failure mode surfaces a clear "temporarily unavailable" message.
 *    No silent fallback to free text.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSession } from "@/contexts/SessionContext";
import { getStoredToken } from "@/lib/authStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHE_TTL_MS = 60_000; // 60s — short, since master data CAN change

// Module-level cache keyed strictly by `${org_id}|${outlet_id}`.
const _cache = new Map(); // key -> { ts, categories }

export function clearVendorCategoriesCache(key) {
  if (key) _cache.delete(key);
  else _cache.clear();
}

export function useVendorCategories() {
  const { me, currentOutletId } = useSession();
  const orgId = me?.org?.id || null;
  const outletId = currentOutletId || null;
  const [state, setState] = useState({
    categories: [],
    loading: false,
    error: null,
    ready: false,
  });
  // Track latest key to avoid races when outlet flips fast
  const latestKey = useRef(null);

  useEffect(() => {
    // PRECONDITIONS:
    //  - login must be complete (me + org)
    //  - outlet must be selected
    if (!orgId || !outletId) {
      setState({ categories: [], loading: false, error: null, ready: false });
      return;
    }
    const key = `${orgId}|${outletId}`;
    latestKey.current = key;

    // Cache hit?
    const cached = _cache.get(key);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setState({ categories: cached.categories, loading: false, error: null, ready: true });
      return;
    }

    setState({ categories: [], loading: true, error: null, ready: false });
    const token = getStoredToken();
    axios
      .get(`${API}/control-room/vendor-categories`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "X-Selected-Outlet-Id": outletId,
        },
        timeout: 12000,
      })
      .then((r) => {
        if (latestKey.current !== key) return; // outlet flipped; ignore stale
        const categories = Array.isArray(r.data) ? r.data : [];
        _cache.set(key, { ts: Date.now(), categories });
        setState({ categories, loading: false, error: null, ready: true });
      })
      .catch((e) => {
        if (latestKey.current !== key) return;
        const detail = e?.response?.data?.detail;
        const code = (detail && typeof detail === "object") ? detail.error_code : null;
        const message =
          (detail && typeof detail === "object") ? detail.message :
          (typeof detail === "string" ? detail : null);
        setState({
          categories: [],
          loading: false,
          ready: true,
          error: {
            code: code || "unknown",
            message: message ||
              "Vendor categories are temporarily unavailable. Please try again later.",
          },
        });
      });
  }, [orgId, outletId]);

  return state;
}
